<!--
  @component ImportFileTab
  @description File import tab for importing bibliography files (BibTeX, RIS, CSL JSON)
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '@/i18n';
  import { fileImportService, type ImportFormat } from '@services/fileImportService';
  import { notify } from '@services/notificationService';
  import ImportDropZone from './ImportDropZone.svelte';
  import ImportRecordList from './ImportRecordList.svelte';
  import type { ItemData, UnifiedRecord } from '@types';

  export let selectedRecords: Set<string> = new Set();

  const dispatch = createEventDispatcher<{
    selectionChange: { selectedRecords: Set<string> };
  }>();

  let selectedFile: File | null = null;
  let fileRecords: UnifiedRecord[] = [];
  let fileParseError: string | null = null;
  let detectedFormat: ImportFormat | null = null;

  // Sync selection changes back to parent
  $: dispatch('selectionChange', { selectedRecords });

  function fileToUnified(records: Partial<ItemData>[]): UnifiedRecord[] {
    return records.map((item, index) => ({
      id: `file-${index}-${Date.now()}`,
      item,
      source: 'file' as const,
      identifiers: {
        isbn: item.ISBN,
        issn: item.ISSN,
      },
    }));
  }

  async function handleFileSelect(event: CustomEvent<{ file: File }>) {
    const file = event.detail.file;
    selectedFile = file;
    fileParseError = null;
    fileRecords = [];
    selectedRecords = new Set();
    detectedFormat = null;

    try {
      const result = await fileImportService.importFile(file);

      if (result.success) {
        fileRecords = fileToUnified(result.records);
        detectedFormat = result.format;
        // Auto-select all records
        selectedRecords = new Set(fileRecords.map((r) => r.id));
        notify.success($t('import.fileParseSuccess', { count: result.records.length }));
      } else {
        fileParseError = result.errors.join('\n');
        notify.error($t('import.fileParseFailed'));
      }
    } catch (error) {
      fileParseError = error instanceof Error ? error.message : 'Unknown error';
      notify.error($t('import.fileParseFailed'));
    }
  }

  function handleClear() {
    selectedFile = null;
    fileRecords = [];
    fileParseError = null;
    detectedFormat = null;
    selectedRecords = new Set();
  }

  // Expose file records for parent
  export function getFileRecords(): UnifiedRecord[] {
    return fileRecords;
  }

  // Reset state (called by parent on close)
  export function reset() {
    handleClear();
  }
</script>

<div class="tab-content">
  <ImportDropZone
    {selectedFile}
    {detectedFormat}
    on:fileSelect={handleFileSelect}
    on:clear={handleClear}
  />

  {#if fileParseError}
    <div class="parse-error">
      <span class="error-icon">⚠️</span>
      <span class="error-text">{fileParseError}</span>
    </div>
  {/if}

  <ImportRecordList records={fileRecords} bind:selectedRecords showThumbnails={false} />
</div>

<style>
  .tab-content {
    min-height: 350px;
  }

  .parse-error {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
    background: rgba(255, 107, 107, 0.1);
    border: 1px solid rgba(255, 107, 107, 0.3);
    border-radius: 4px;
    margin-bottom: 16px;
  }

  .error-icon {
    flex-shrink: 0;
  }

  .error-text {
    font-size: 13px;
    color: var(--color-error, #ff6b6b);
    white-space: pre-wrap;
  }
</style>
